import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';

const MAX_ITEMS = 8;

const formatUkDateTime = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const resolveReference = (preorder) => {
  if (!preorder) return '—';
  return preorder.publicRef || preorder.reference || preorder.id || '—';
};

const extractItems = (preorder) => {
  if (!preorder || !Array.isArray(preorder.items)) return [];
  return preorder.items.map((item) => ({
    name: item.productName || item.name || 'Item',
    quantity: item.quantity || 0,
  }));
};

export default function CustomerPreorderConfirmationPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currencyCode = useCurrencyCode();
  const [preorder, setPreorder] = useState(location.state?.preorder || null);
  const [loading, setLoading] = useState(!location.state?.preorder);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const fetchConfirmation = async () => {
      if (preorder || !id) return;
      setLoading(true);
      try {
        const res = await api.get(`/preorders/${id}/confirmation`);
        setPreorder(res.data || null);
        setLoadFailed(false);
      } catch (err) {
        console.error('Failed to load preorder confirmation', err);
        toast.error('Unable to load your confirmation right now.');
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    fetchConfirmation();
  }, [id, preorder]);

  const reference = resolveReference(preorder);
  const items = useMemo(() => extractItems(preorder), [preorder]);
  const displayedItems = items.slice(0, MAX_ITEMS);
  const hiddenCount = Math.max(0, items.length - MAX_ITEMS);
  const totalAmount = Number(preorder?.grandTotal || preorder?.totalAmount || 0);
  const deliveryOption = preorder?.deliveryOption || 'delivery';
  const pickupSlot = preorder?.pickupSlot || preorder?.metadata?.pickupSlot || null;

  const handleCopyReference = async () => {
    const value = String(reference);
    if (!value || value === '—') return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Copied');
    } catch (err) {
      console.error('Failed to copy reference', err);
      toast.error('Unable to copy reference.');
    }
  };

  const handleTrackOrder = () => {
    if (reference && reference !== '—') {
      navigate(`/preorders/track?ref=${encodeURIComponent(reference)}`);
      return;
    }
    navigate('/preorders/track');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 pb-24 pt-10 text-white">
        <main className="mx-auto flex max-w-3xl flex-col gap-4">
          <h1 className="text-2xl font-semibold">Loading your confirmation…</h1>
        </main>
      </div>
    );
  }

  if (!preorder || loadFailed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 pb-24 pt-10 text-white">
        <main className="mx-auto flex max-w-3xl flex-col gap-4">
          <h1 className="text-2xl font-semibold">We couldn’t load your confirmation</h1>
          <p className="text-sm text-slate-400">
            Please return to the preorder page and try again.
          </p>
          <button
            type="button"
            onClick={() => navigate('/preorders/new')}
            className="w-full rounded-2xl bg-emerald-400 px-5 py-3 text-sm font-semibold text-slate-900"
          >
            Back to new order
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 pb-24 pt-10 text-white">
      <main className="mx-auto flex max-w-3xl flex-col gap-6">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.5em] text-slate-500">Ofoodi Local</p>
          <h1 className="text-3xl font-semibold">Order received</h1>
          <p className="text-sm text-slate-400">
            We’ve received your pre-order and we’ll notify you when it’s ready for pickup.
          </p>
        </header>

        <section className="rounded-3xl border border-slate-800/70 bg-slate-900/70 p-5 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Reference</p>
              <p className="text-2xl font-semibold text-white">{reference}</p>
            </div>
            <button
              type="button"
              onClick={handleCopyReference}
              className="rounded-2xl border border-emerald-400/60 bg-emerald-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-emerald-200"
            >
              Copy reference
            </button>
          </div>

          <div className="mt-4 grid gap-4 border-t border-slate-800/70 pt-4 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Received</p>
              <p className="text-sm text-white">{formatUkDateTime(preorder.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Items</p>
              <p className="text-sm text-white">{items.length}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Total</p>
              <p className="text-sm text-white">{formatCurrency(totalAmount, currencyCode)}</p>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-slate-800/70 bg-slate-950/40 p-4 text-sm text-slate-300">
            <p>
              {deliveryOption === 'collection'
                ? 'Pickup at store'
                : 'Delivery to your address'}
            </p>
            {deliveryOption === 'collection' && pickupSlot && (
              <p className="mt-1 text-xs text-slate-400">Pickup window: {pickupSlot}</p>
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-slate-800/70 bg-slate-900/70 p-5">
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Items</p>
          <div className="mt-3 space-y-2">
            {displayedItems.map((item, index) => (
              <div
                key={`${item.name}-${index}`}
                className="flex items-center justify-between text-sm text-slate-200"
              >
                <span>{item.name}</span>
                <span className="text-slate-400">x{item.quantity}</span>
              </div>
            ))}
            {hiddenCount > 0 && (
              <p className="text-xs text-slate-400">+{hiddenCount} more</p>
            )}
          </div>
        </section>

        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleTrackOrder}
            className="rounded-2xl border border-slate-700 px-4 py-3 text-sm font-semibold text-slate-200"
          >
            Track order
          </button>
          <button
            type="button"
            onClick={() => navigate('/preorders/new')}
            className="rounded-2xl bg-emerald-400 px-4 py-3 text-sm font-semibold text-slate-900"
          >
            Place another order
          </button>
        </div>
      </main>
    </div>
  );
}
